import React, { useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'bk-web-app/ClientContext';
import useLedgerAccounts from '../ledger-accounts/useLedgerAccounts';
import { WrappedSpinner } from '../../spinner';
import { NullState } from 'bench-internal-theme';
import { PersonalTransactionsView } from './components/personal-transactions-view/PersonalTransactionsView';
import { PersonalBusinessReviewView } from './components/pb-review-view';
import type { LedgerAccount, DateRange } from './types';

const PERSONAL_BANKING_CATEGORY_ID = '9';

const isPbLedgerAccount = (ledgerAccount: LedgerAccount): boolean =>
  ledgerAccount.category?.id === PERSONAL_BANKING_CATEGORY_ID;

const getPersonalBankingLedgerAccounts = (ledgerAccounts: LedgerAccount[]): LedgerAccount[] =>
  ledgerAccounts.filter(isPbLedgerAccount);

const ModeToggleContainer = styled.div`
  display: flex;
  gap: 0;
  margin: 16px 16px 0;
  padding: 0 24px;
  border-bottom: 1px solid #e1e5e9;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  appearance: button;
  background: transparent;
  border: none;
  border-bottom: ${props => (props.$active ? '2px solid #098b8c' : '2px solid transparent')};
  box-shadow: ${props => (props.$active ? 'rgb(9, 139, 140) 0px 1px 0px 0px' : 'none')};
  color: ${props => (props.$active ? '#098b8c' : '#6b7280')};
  cursor: pointer;
  display: block;
  font-family: LLCircularWeb, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  height: 36px;
  line-height: 16px;
  margin: 0;
  outline: none;
  padding: 8px 24px;
  position: relative;
  text-align: center;
  text-decoration: none;
  text-shadow: ${props => (props.$active ? 'rgba(0, 0, 0, 0.1) 0px 0px 4px' : 'none')};
  transition: background 0.185s linear, color 0.185s linear, box-shadow 0.185s linear;
  user-select: none;
  -webkit-font-smoothing: antialiased;

  &:hover {
    background-color: ${props => (props.$active ? 'transparent' : 'rgba(9, 139, 140, 0.05)')};
    color: ${props => (props.$active ? '#098b8c' : '#098b8c')};
  }

  &:focus {
    outline: none;
  }

  /* Adjust margin to align with container border */
  margin-bottom: -1px;
`;

type ViewMode = 'unarchive' | 'review';

type PersonalTransactionsContainerProps = {
  dateRange?: DateRange;
  onTransactionCountChange?: (count: number) => void;
  isPersonalBusinessEnabled?: boolean;
};

const PersonalTransactionsContainer = ({
  dateRange,
  onTransactionCountChange,
  isPersonalBusinessEnabled = false,
}: PersonalTransactionsContainerProps) => {
  const { ledgerAccounts, hasError, isLoading } = useLedgerAccounts();
  const client = useClient();
  const [viewMode, setViewMode] = useState<ViewMode>('review');

  const handleTransactionCountChange = (count: number) => {
    if (onTransactionCountChange) {
      onTransactionCountChange(count);
    }
  };

  if (isLoading) {
    return <WrappedSpinner />;
  }

  if (hasError) {
    return (
      <NullState
        bigEmoji="🙈"
        description={hasError?.message}
        title="Uh oh! The following error was encountered while fetching the data for this page."
      />
    );
  }

  const personalBankingLedgerAccounts = getPersonalBankingLedgerAccounts(ledgerAccounts);

  if (personalBankingLedgerAccounts.length === 0) {
    return (
      <NullState
        bigEmoji="📁"
        description="No personal transactions available"
        title="The customer has no personal accounts used for business."
      />
    );
  }

  if (!isPersonalBusinessEnabled) {
    return (
      <PersonalTransactionsView client={client} ledgerAccounts={personalBankingLedgerAccounts} />
    );
  }

  return (
    <>
      <ModeToggleContainer>
        <ModeButton $active={viewMode === 'review'} onClick={() => setViewMode('review')}>
          Review PB Transactions
        </ModeButton>
        <ModeButton $active={viewMode === 'unarchive'} onClick={() => setViewMode('unarchive')}>
          Unarchive Personal Transactions
        </ModeButton>
      </ModeToggleContainer>

      {viewMode === 'review' ? (
        <PersonalBusinessReviewView
          clientId={client.id.toString()}
          startDate={
            dateRange?.startDate
              ? new Date(dateRange.startDate)
              : new Date(new Date().getFullYear(), 0, 1)
          }
          endDate={dateRange?.endDate ? new Date(dateRange.endDate) : new Date()}
          onTransactionCountChange={handleTransactionCountChange}
        />
      ) : (
        <PersonalTransactionsView client={client} ledgerAccounts={personalBankingLedgerAccounts} />
      )}
    </>
  );
};

export default PersonalTransactionsContainer;
